import React from 'react';

import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

import { StateCalculation } from '../../@types/State';

const getTableRowDualValue = (
  name: string,
  v1: number | string,
  v2?: number | string
) => {
  return (
    <TableRow
      key={name}
      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
    >
      <TableCell component='th' scope='row'>
        {name}
      </TableCell>
      <TableCell align='right'>{v1}</TableCell>
      {v2 && <TableCell align='right'>{v2}</TableCell>}
    </TableRow>
  );
};

const getTableRowPerAcre = (name: string, v1: number, acres: number) => {
  const pa = Math.floor((v1 / acres) * 100) / 100;
  const valueToDisplay = acres ? `${v1} (${pa})` : v1;
  return (
    <TableRow
      key={name}
      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
    >
      <TableCell component='th' scope='row'>
        {name}
      </TableCell>
      <TableCell align='right'>{valueToDisplay}</TableCell>
    </TableRow>
  );
};

interface Props {
  state: StateCalculation;
  showCalcs: boolean;
}
const StateCalculations: React.FC<Props> = ({ state, showCalcs }) => {
  if (!showCalcs) return null;
  return (
    <Grid container sx={{ mx: { xs: 0, sm: 2 }, mt: 2 }} columns={12}>
      <Grid
        size={{
          xs: 12,
          sm: 6
        }}>
        <TableContainer component={Paper}>
          <Table size='small' aria-label='a dense table'>
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: 'bold' }}>People</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getTableRowDualValue('Living Space raw', state.rawLivingSpace)}
              {getTableRowDualValue('Living Space mod', state.modLivingSpace)}
              {getTableRowPerAcre(
                'Peasants',
                state.calculatedEconomy.peasants,
                state.acres
              )}
              {getTableRowPerAcre('Army', state.armyCalc.total, state.acres)}
              {getTableRowPerAcre(
                'Thieves',
                state.armyCalc.thieves,
                state.acres
              )}
              {getTableRowPerAcre(
                'Wizards',
                state.armyCalc.wizards,
                state.acres
              )}
              {getTableRowPerAcre('Peons', state.modLivingSpace, state.acres)}
              {getTableRowPerAcre(
                'Jobs',
                state.calculatedEconomy.availableJobs,
                state.acres
              )}
              {getTableRowPerAcre(
                'Workers',
                state.calculatedEconomy.availableWorkers,
                state.acres
              )}
              {getTableRowPerAcre(
                'Min Workers',
                state.calculatedEconomy.optimalWorkers,
                state.acres
              )}
              {getTableRowDualValue(
                '% Jobs Performed',
                state.calculatedEconomy.jobsPerformedPcnt
              )}
              {getTableRowPerAcre(
                'Employed',
                state.calculatedEconomy.employed,
                state.acres
              )}
              {getTableRowPerAcre(
                'UnfilledJobs',
                state.calculatedEconomy.unfilledJobs,
                state.acres
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size='small' aria-label='a dense table'>
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: 'bold' }}>Economy</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getTableRowDualValue('BE est', state.estBE)}
              {getTableRowDualValue(
                'BE used in calc',
                Math.round(state.beUsed)
              )}
              {getTableRowDualValue('Income mod', state.modIncome)}
              {getTableRowDualValue('Income raw', state.rawIncome)}
              {getTableRowDualValue('Banks Bonus', state.gcFromBanks)}
              {getTableRowDualValue(
                'Banks Income Mod',
                state.banksIncomeModifier
              )}
              {getTableRowDualValue('Wages', state.wages)}
              {getTableRowDualValue('Arms Wages Mod', state.armsWagesModifier)}
              {getTableRowDualValue(
                'Arms Training Cost Mod',
                state.armsTrainingModifier
              )}
              {getTableRowDualValue(
                'Total Draft Cost per tick',
                state.draftCost
              )}
              {getTableRowDualValue('Net Income', state.netIncome)}
              {getTableRowDualValue(
                'Construction Cost',
                state.constructionCost
              )}
              {getTableRowDualValue('Raze Cost', state.razeCost)}
              {getTableRowDualValue(
                'Draft Cost Per Sold',
                state.draftCostPerSold
              )}
              {getTableRowDualValue('bushelsPerTick', state.bushelsPerTick)}
              {getTableRowDualValue('runesPerTick', state.runesPerTick)}
              {getTableRowDualValue('Soldiers Per Tick', state.soldiersPerTick)}
              {getTableRowDualValue('Wizards Per Tick', state.wizardsperTick)}
              {getTableRowDualValue('Arms Draft Mod', state.armsDraftModifier)}
              {getTableRowDualValue(
                'Mod Draft Speed',
                `${Math.floor(state.modDraftSpeed * 100000) / 1000}%`
              )}
              {getTableRowDualValue(
                'Current Draft Level Factor',
                state.currentDraftLevelFactor
              )}
              {getTableRowDualValue(
                'Ticks till draft complete',
                state.draftTicks
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6
        }}>
        <TableContainer component={Paper}>
          <Table size='small' aria-label='a dense table'>
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: 'bold' }}>Military</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getTableRowDualValue(
                'Base ME',
                `${Math.floor(state.baseME * 100) / 100}%`
              )}
              {getTableRowDualValue(
                'Off ME',
                `${Math.floor(state.oME * 100) / 100}%`
              )}
              {getTableRowDualValue(
                'Def ME',
                `${Math.floor(state.dME * 100) / 100}%`
              )}
              {getTableRowDualValue('Defense raw', state.rawDef)}
              {getTableRowDualValue('Defense mod', state.modDef)}
              {getTableRowDualValue('Offense raw', state.rawOff)}
              {getTableRowDualValue('Offense mod', state.modOff)}
            </TableBody>
          </Table>
        </TableContainer>

        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size='small' aria-label='a dense table'>
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: 'bold' }}>
                  End of Max Draft ({state.targetDraft}%)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getTableRowDualValue(
                'Current Draft',
                `${state.calculatedEconomy.currentDraft}%`
              )}
              {getTableRowPerAcre(
                'Target Army',
                state.targetMilitary - state.armyCalc.thieves,
                state.acres
              )}
              {getTableRowDualValue('Ticks to fill draft', state.draftTicksMin)}
              {getTableRowPerAcre(
                'Peasants',
                state.calculatedEconomy.peasants - state.recruitsNeeded,
                state.acres
              )}
              {getTableRowPerAcre(
                'New Recruits',
                state.recruitsNeeded,
                state.acres
              )}
              {getTableRowPerAcre(
                'Total Solds (inc training)',
                state.recruitsNeeded +
                  state.armyCalc.soldiers +
                  state.armyCalc.soldiersTraining,
                state.acres
              )}
              {getTableRowDualValue(
                'Total Train Credits',
                state.trainingCalc.trainCredits
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size='small' aria-label='a dense table'>
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: 'bold' }}>
                  Bonuses and Mods
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getTableRowDualValue(
                'TG OME Bonus %',
                state.trainingGroundsOMEBonus
              )}
              {getTableRowDualValue('Forts DME Bonus %', state.fortsDMEBonus)}
              {getTableRowDualValue(
                'Castles Protection Bonus %',
                state.castlesProtectionBonus
              )}
              {getTableRowDualValue('Arms Draft Mod', state.armsDraftModifier)}
              {getTableRowDualValue('Arms Wages Mod', state.armsWagesModifier)}
              {getTableRowDualValue(
                'Arms Training Mod',
                state.armsTrainingModifier
              )}
              {getTableRowDualValue('Homes Bonus', state.homesBonus)}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
};

export default StateCalculations;
